import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, BookImage, CreditCard, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TOSS_BILLING_CLIENT_KEY } from '@/config/toss';
import './custom-c2.css';

const BILLING_TEST_BASE = '/custom/c2/billing-test';
const BILLING_SESSION_KEY = 'custom-c2-photobook-billing';
const BILLING_TEST_AMOUNT = 29900;
const COMPANY_PHONE = '070-8633-0413';
const COMPANY_PHONE_TEL = 'tel:07086330413';
const PRODUCT_COVER = '/images/custom-c2/photobook-hero-cover.webp';
const PRODUCT_STEPS = [
  {
    image: '/images/custom-c2/photobook-step-photos.webp',
    textKey: 'customC2.billingTest.stepPhotos',
  },
  {
    image: '/images/custom-c2/photobook-step-make.webp',
    textKey: 'customC2.billingTest.stepMake',
  },
  {
    image: '/images/custom-c2/photobook-step-monthly.webp',
    textKey: 'customC2.billingTest.stepMonthly',
  },
] as const;

type BillingTestScreen = 'start' | 'success' | 'fail';
type ChargeStatus = 'confirming' | 'done' | 'error';
type ContactField = 'name' | 'phone' | 'email';
type ContactErrors = Partial<Record<ContactField, string>>;

type StoredBillingSession = {
  customerKey: string;
  billingKey: string;
  lastAuthKey: string | null;
  lastPayment: ChargedPayment | null;
};

type ChargedPayment = {
  billingKey: string;
  customerKey: string;
  orderId: string;
  totalAmount: number;
  method: string;
  approvedAt: string;
  receiptUrl: string | null;
  cardLast4: string | null;
};

const normalizePathname = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

const resolveScreen = (pathname: string): BillingTestScreen => {
  const path = normalizePathname(pathname);
  if (path === `${BILLING_TEST_BASE}/success`) {
    return 'success';
  }
  if (path === `${BILLING_TEST_BASE}/fail`) {
    return 'fail';
  }
  return 'start';
};

const readSession = (): StoredBillingSession | null => {
  try {
    const raw = window.sessionStorage.getItem(BILLING_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StoredBillingSession>;
    if (typeof parsed.customerKey !== 'string' || typeof parsed.billingKey !== 'string') {
      return null;
    }
    return {
      customerKey: parsed.customerKey,
      billingKey: parsed.billingKey,
      lastAuthKey: typeof parsed.lastAuthKey === 'string' ? parsed.lastAuthKey : null,
      lastPayment: parsed.lastPayment ?? null,
    };
  } catch {
    return null;
  }
};

const writeSession = (session: StoredBillingSession): void => {
  window.sessionStorage.setItem(BILLING_SESSION_KEY, JSON.stringify(session));
};

const isLikelyPhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return /^01\d{8,9}$/.test(digits);
};

const isLikelyEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const formatPhoneInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length < 4) {
    return digits;
  }
  if (digits.length < 8) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  if (digits.length < 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

const revealFieldAboveKeyboard = (element: HTMLElement | null): void => {
  window.setTimeout(() => {
    element?.scrollIntoView({ block: 'center', behavior: 'auto' });
  }, 320);
};

const BusinessFooter: React.FC = () => {
  const { t } = useTranslation();
  return (
    <footer className="custom-c2-legal-footer">
      <p>{t('customC2.billingTest.legalCompany', '주식회사 제제미미 대표이사 박미영')}</p>
      <p>{t('customC2.billingTest.legalBizNo', '사업자등록번호: 434-81-01803')}</p>
      <p>{t('customC2.billingTest.legalMailOrder', '통신판매: 제2025-서울영등포-2692호')}</p>
      <p>{t('customC2.billingTest.legalAddress', '주소 : 서울특별시 영등포구 의사당대로 83, 오투타워 서울핀테크랩 4,5,6,8,17,19층(여의도동)')}</p>
      <p>
        {t('customC2.billingTest.legalPhoneLabel', '전화번호:')}
        {' '}
        <a href={COMPANY_PHONE_TEL}>{COMPANY_PHONE}</a>
      </p>
    </footer>
  );
};

const chargeBilling = async (body: { customerKey: string; authKey?: string; billingKey?: string }): Promise<ChargedPayment> => {
  const response = await fetch('/api/custom-c2-billing-charge', {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as Partial<ChargedPayment> & { message?: string };
  if (!response.ok) {
    throw new Error(payload.message ?? `Billing charge failed with status ${response.status}`);
  }
  if (typeof payload.billingKey !== 'string' || typeof payload.customerKey !== 'string') {
    throw new Error('Billing charge returned an incomplete payload');
  }
  return {
    billingKey: payload.billingKey,
    customerKey: payload.customerKey,
    orderId: typeof payload.orderId === 'string' ? payload.orderId : '',
    totalAmount: typeof payload.totalAmount === 'number' ? payload.totalAmount : BILLING_TEST_AMOUNT,
    method: typeof payload.method === 'string' ? payload.method : '',
    approvedAt: typeof payload.approvedAt === 'string' ? payload.approvedAt : '',
    receiptUrl: typeof payload.receiptUrl === 'string' ? payload.receiptUrl : null,
    cardLast4: typeof payload.cardLast4 === 'string' ? payload.cardLast4 : null,
  };
};

const StartScreen: React.FC = () => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<ContactErrors>({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [session, setSession] = useState<StoredBillingSession | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSession(readSession());
  }, []);

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const clearError = (field: ContactField): void => {
    setErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const validateContact = (): ContactErrors => {
    const nextErrors: ContactErrors = {};
    if (name.trim().length < 2) {
      nextErrors.name = t('customC2.validation.name');
    }
    if (!isLikelyPhone(phone)) {
      nextErrors.phone = t('customC2.validation.phone');
    }
    if (!isLikelyEmail(email)) {
      nextErrors.email = t('customC2.validation.email');
    }
    return nextErrors;
  };

  const registerCard = async (): Promise<void> => {
    const nextErrors = validateContact();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstInvalid = (['name', 'phone', 'email'] as ContactField[]).find((field) => nextErrors[field]);
      const fieldRef = firstInvalid === 'phone' ? phoneRef : firstInvalid === 'email' ? emailRef : nameRef;
      revealFieldAboveKeyboard(fieldRef.current);
      return;
    }

    setBusy(true);
    try {
      const { loadTossPayments } = await import('@tosspayments/tosspayments-sdk');
      const customerKey = `c2b-${crypto.randomUUID()}`;
      const tossPayments = await loadTossPayments(TOSS_BILLING_CLIENT_KEY);
      const payment = tossPayments.payment({ customerKey });
      await payment.requestBillingAuth({
        method: 'CARD',
        customerName: name.trim(),
        customerEmail: email.trim(),
        successUrl: `${window.location.origin}${BILLING_TEST_BASE}/success`,
        failUrl: `${window.location.origin}${BILLING_TEST_BASE}/fail`,
        windowTarget: 'self',
      });
    } catch (error) {
      console.error('Toss billing auth did not complete', error);
      setToast(t('customC2.billingTest.loadFailed'));
      setBusy(false);
    }
  };

  const chargeAgain = async (): Promise<void> => {
    if (!session) {
      return;
    }
    setBusy(true);
    try {
      const payment = await chargeBilling({
        customerKey: session.customerKey,
        billingKey: session.billingKey,
      });
      const nextSession: StoredBillingSession = {
        ...session,
        lastPayment: payment,
      };
      writeSession(nextSession);
      setSession(nextSession);
    } catch (error) {
      console.error('Toss billing repeat charge failed', error);
      setToast(error instanceof Error ? error.message : t('customC2.billingTest.chargeFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="custom-c2-form-page custom-c2-photobook">
      {toast ? <div className="custom-c2-toast" role="status" aria-live="polite">{toast}</div> : null}
      <p className="custom-c2-kicker">{t('customC2.billingTest.kicker')}</p>
      <h1>{t('customC2.billingTest.title')}</h1>
      <p className="custom-c2-success-lead">{t('customC2.billingTest.lead')}</p>

      <div className="custom-c2-photobook-stage">
        <img
          className="custom-c2-photobook-hero"
          src={PRODUCT_COVER}
          alt={t('customC2.billingTest.heroAlt')}
        />
        <span className="custom-c2-photobook-badge">{t('customC2.billingTest.autoBadge')}</span>
      </div>

      <p className="custom-c2-photobook-price">
        <strong>{t('customC2.billingTest.price')}</strong>
        <span>{t('customC2.billingTest.priceUnit')}</span>
      </p>

      <ol className="custom-c2-photobook-steps">
        {PRODUCT_STEPS.map((step) => (
          <li key={step.textKey}>
            <img src={step.image} alt="" />
            <span>{t(step.textKey)}</span>
          </li>
        ))}
      </ol>

      <form
        id="billing-test-form"
        className="custom-c2-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void registerCard();
        }}
      >
        <label className="custom-c2-field" htmlFor="billing-test-name">
          <span>{t('customC2.gift.nameLabel')}</span>
          <input
            ref={nameRef}
            id="billing-test-name"
            autoComplete="name"
            maxLength={50}
            required
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'billing-test-name-error' : undefined}
            placeholder={t('customC2.gift.namePlaceholder')}
            value={name}
            onFocus={() => revealFieldAboveKeyboard(nameRef.current)}
            onChange={(event) => {
              setName(event.target.value);
              clearError('name');
            }}
          />
          {errors.name ? <small id="billing-test-name-error" className="custom-c2-field-error">{errors.name}</small> : null}
        </label>

        <label className="custom-c2-field" htmlFor="billing-test-phone">
          <span>{t('customC2.gift.phoneLabel')}</span>
          <input
            ref={phoneRef}
            id="billing-test-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? 'billing-test-phone-error' : undefined}
            placeholder={t('customC2.gift.phonePlaceholder')}
            value={phone}
            onFocus={() => revealFieldAboveKeyboard(phoneRef.current)}
            onChange={(event) => {
              setPhone(formatPhoneInput(event.target.value));
              clearError('phone');
            }}
          />
          {errors.phone ? <small id="billing-test-phone-error" className="custom-c2-field-error">{errors.phone}</small> : null}
        </label>

        <label className="custom-c2-field" htmlFor="billing-test-email">
          <span>{t('customC2.billingTest.emailLabel')}</span>
          <input
            ref={emailRef}
            id="billing-test-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={80}
            required
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'billing-test-email-error' : undefined}
            placeholder={t('customC2.billingTest.emailPlaceholder')}
            value={email}
            onFocus={() => revealFieldAboveKeyboard(emailRef.current)}
            onChange={(event) => {
              setEmail(event.target.value);
              clearError('email');
            }}
          />
          {errors.email ? <small id="billing-test-email-error" className="custom-c2-field-error">{errors.email}</small> : null}
        </label>
      </form>

      {session?.lastPayment ? (
        <p className="custom-c2-payment-hint">
          {t('customC2.billingTest.lastCharge', { amount: session.lastPayment.totalAmount.toLocaleString() })}
        </p>
      ) : null}

      <BusinessFooter />

      <div className="custom-c2-form-dock">
        <button
          type="submit"
          form="billing-test-form"
          className="custom-c2-primary-button"
          disabled={busy}
        >
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <BookImage aria-hidden="true" />}
          {t('customC2.billingTest.register')}
          <ArrowRight aria-hidden="true" />
        </button>
        {session ? (
          <button
            type="button"
            className="custom-c2-text-button"
            disabled={busy}
            onClick={() => void chargeAgain()}
          >
            <CreditCard aria-hidden="true" />
            {t('customC2.billingTest.chargeAgain')}
          </button>
        ) : null}
      </div>
    </main>
  );
};

const SuccessScreen: React.FC<{ onHome: () => void }> = ({ onHome }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ChargeStatus>('confirming');
  const [payment, setPayment] = useState<ChargedPayment | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) {
      return;
    }
    requestedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const authKey = params.get('authKey')?.trim() ?? '';
    const customerKey = params.get('customerKey')?.trim() ?? '';
    const existing = readSession();
    if (existing && existing.lastAuthKey === authKey && existing.lastPayment) {
      setPayment(existing.lastPayment);
      setStatus('done');
      return;
    }
    if (!authKey || !customerKey) {
      setStatus('error');
      return;
    }

    const confirmCharge = async () => {
      try {
        const charged = await chargeBilling({ customerKey, authKey });
        writeSession({
          customerKey: charged.customerKey,
          billingKey: charged.billingKey,
          lastAuthKey: authKey,
          lastPayment: charged,
        });
        setPayment(charged);
        setStatus('done');
      } catch (error) {
        console.error('Failed to confirm photobook billing charge', error);
        setErrorMessage(error instanceof Error ? error.message : '');
        setStatus('error');
      }
    };

    void confirmCharge();
  }, []);

  if (status === 'confirming') {
    return (
      <main className="custom-c2-form-page custom-c2-success">
        <p className="custom-c2-kicker">{t('customC2.billingTest.kicker')}</p>
        <p className="custom-c2-payment-loading">{t('customC2.billingTest.confirming')}</p>
      </main>
    );
  }

  if (status === 'error' || !payment) {
    return (
      <main className="custom-c2-form-page custom-c2-success">
        <p className="custom-c2-kicker">{t('customC2.billingTest.kicker')}</p>
        <h1>{t('customC2.paymentSuccess.errorTitle')}</h1>
        <p className="custom-c2-success-lead">
          {t('customC2.billingTest.chargeFailed')}
          {errorMessage ? `\n(${errorMessage})` : ''}
        </p>
        <button type="button" className="custom-c2-text-button" onClick={onHome}>
          {t('customC2.billingTest.home')}
        </button>
        <BusinessFooter />
      </main>
    );
  }

  return (
    <main className="custom-c2-form-page custom-c2-success">
      <p className="custom-c2-kicker">{t('customC2.paymentSuccess.kicker')}</p>
      <h1>{t('customC2.billingTest.doneTitle')}</h1>
      <p className="custom-c2-success-lead">{t('customC2.billingTest.doneLead')}</p>
      <img
        className="custom-c2-photobook-hero custom-c2-photobook-hero-done"
        src="/images/custom-c2/photobook-step-monthly.webp"
        alt=""
      />
      <dl className="custom-c2-payment-detail">
        <div>
          <dt>{t('customC2.paymentSuccess.amountLabel')}</dt>
          <dd>{t('customC2.paymentSuccess.amountValue', { amount: payment.totalAmount.toLocaleString() })}</dd>
        </div>
        {payment.method ? (
          <div>
            <dt>{t('customC2.paymentSuccess.methodLabel')}</dt>
            <dd>{payment.method}{payment.cardLast4 ? ` · ${payment.cardLast4}` : ''}</dd>
          </div>
        ) : null}
      </dl>
      {payment.receiptUrl ? (
        <a className="custom-c2-receipt-link" href={payment.receiptUrl} target="_blank" rel="noopener noreferrer">
          {t('customC2.paymentSuccess.receipt')}
        </a>
      ) : null}
      <button type="button" className="custom-c2-text-button" onClick={onHome}>
        {t('customC2.billingTest.home')}
      </button>
      <BusinessFooter />
    </main>
  );
};

const FailScreen: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  const failMessage = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('message') ?? '';
  }, []);

  return (
    <main className="custom-c2-form-page custom-c2-success">
      <p className="custom-c2-kicker">{t('customC2.paymentFail.kicker')}</p>
      <h1>{t('customC2.paymentFail.title')}</h1>
      <p className="custom-c2-success-lead">
        {t('customC2.paymentFail.lead')}
        {failMessage ? `\n(${failMessage})` : ''}
      </p>
      <BusinessFooter />
      <div className="custom-c2-form-dock">
        <button type="button" className="custom-c2-primary-button" onClick={onRetry}>
          {t('customC2.paymentFail.retry')}
          <ArrowRight aria-hidden="true" />
        </button>
      </div>
    </main>
  );
};

export const isCustomC2BillingTestPath = (pathname: string): boolean => {
  const path = normalizePathname(pathname);
  return path === BILLING_TEST_BASE
    || path === `${BILLING_TEST_BASE}/success`
    || path === `${BILLING_TEST_BASE}/fail`;
};

export const CustomC2BillingTestView: React.FC = () => {
  const [pathname, setPathname] = useState(() => (
    typeof window === 'undefined' ? BILLING_TEST_BASE : window.location.pathname
  ));
  const screen = useMemo(() => resolveScreen(pathname), [pathname]);

  useEffect(() => {
    const syncPathname = () => {
      setPathname(window.location.pathname);
    };
    window.addEventListener('popstate', syncPathname);
    return () => {
      window.removeEventListener('popstate', syncPathname);
    };
  }, []);

  const go = (path: string): void => {
    window.history.pushState({}, '', path);
    setPathname(path);
  };

  return (
    <div className="custom-c2">
      <div className="custom-c2-shell">
        {screen === 'success' ? (
          <SuccessScreen onHome={() => go(BILLING_TEST_BASE)} />
        ) : screen === 'fail' ? (
          <FailScreen onRetry={() => go(BILLING_TEST_BASE)} />
        ) : (
          <StartScreen />
        )}
      </div>
    </div>
  );
};
